import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { GenericForm } from '@neo/blocks/forms/generic-form'
import { Button } from '@neo/index'
import { Dialog, DialogContent } from '@neo/primitives/dialog'
import type { AxiosError } from 'axios'
import { useRef, useState } from 'react'
import type { GenerateCodeResponse } from 'v2/pages/flow/sidebar/types'
import { CodeEditor } from 'v2/shared/components/code-editor'
import { EmptyState } from 'v2/shared/components/empty-state'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { errorToast } from 'v2/utils/toasts'
import { useGenerateCodeMutation } from '../../queries'
import {
  type CodeGenerationFormValues,
  codeGenerationFormFields,
} from './code-generation-form.config'

interface CodeGenerationModalProps {
  isOpen: boolean
  onClose: () => void
  name: string
  description: string
  onApply: (
    code: string,
    schema: Record<string, unknown>,
    name: string,
    description: string,
  ) => void
}

export const CodeGenerationModal = ({
  isOpen,
  onClose,
  name,
  description,
  onApply,
}: CodeGenerationModalProps) => {
  const formValueRef = useRef<CodeGenerationFormValues>({
    code_instructions: '',
  })

  const [response, setResponse] = useState<GenerateCodeResponse | null>(null)
  const { mutate: generateCode, isPending: isGeneratingCode } = useGenerateCodeMutation()

  const handleGenerateCode = (values: CodeGenerationFormValues) => {
    formValueRef.current = values
    generateCode(
      {
        code_instructions: values.code_instructions,
        function_name: name,
        function_description: description,
      },
      {
        onSuccess: (data: GenerateCodeResponse) => {
          setResponse(data)
        },
        onError: (error) => {
          errorToast({
            title: 'Failed to generate code',
            description: getErrorText(error as AxiosError),
          })
        },
      },
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-[900px] sm:min-h-[600px] sm:max-h-[90vh] p-0 overflow-hidden h-full"
        showCloseButton={false}
      >
        <div className="flex h-full max-h-full overflow-hidden">
          {/* Left side - Code Instructions */}
          <div className="w-1/2 p-6 flex flex-col">
            <h2 className="text-h4-500 mb-4">Generate code</h2>
            <GenericForm<CodeGenerationFormValues>
              id="code-generation-form"
              fields={codeGenerationFormFields}
              onSubmit={handleGenerateCode}
              defaultValues={formValueRef.current}
              isSubmitting={isGeneratingCode}
              submitText="Generate"
              className="flex-0 gap-0"
            />
          </div>

          {/* Vertical divider */}
          <div className="w-px bg-st2 h-full" />

          {/* Right side - Code Preview */}
          <div className="w-1/2 flex flex-col h-full overflow-hidden">
            {response ? (
              <>
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-h4-500 text-foreground">
                        Code Preview{' '}
                        <span className="font-jetbrains text-xs-400 text-secondary">(Python3)</span>
                      </h2>
                      <ClipboardCopyButton
                        textToCopy={response.code}
                        tooltipText="Copy generated code"
                        data-testid="button-copy-code"
                      />
                    </div>
                    <CodeEditor
                      language="python"
                      value={response.code}
                      onChange={() => {}}
                      className="border border-st2 rounded-md min-h-[200px] max-h-[300px] overflow-auto"
                      data-testid="code-editor-generated"
                    />

                    {response.function_schema && (
                      <>
                        <div className="flex items-center justify-between">
                          <h2 className="text-h4-500 text-foreground">
                            Function Schema{' '}
                            <span className="font-jetbrains text-xs-400 text-secondary">
                              (JSON)
                            </span>
                          </h2>
                          <ClipboardCopyButton
                            textToCopy={JSON.stringify(response.function_schema, null, 2)}
                            tooltipText="Copy schema"
                            data-testid="button-copy-schema"
                          />
                        </div>
                        <CodeEditor
                          language="json"
                          value={JSON.stringify(response.function_schema, null, 2)}
                          onChange={() => {}}
                          className="border border-st2 rounded-md min-h-[150px] max-h-[250px] overflow-auto"
                          data-testid="code-editor-schema"
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="flex justify-end p-6 flex-shrink-0 bg-background">
                  <Button
                    onClick={() => {
                      onApply(
                        response.code,
                        response.function_schema,
                        response.function_name,
                        response.function_description,
                      )
                      onClose()
                    }}
                    data-testid="button-use-code"
                  >
                    Apply code
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full w-[270px] mx-auto text-center p-6">
                <EmptyState
                  text="Describe your use case on the left, the code preview will show here."
                  type="empty_app_data"
                />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
